import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    EmailTemplateBreadcrumbs,
    EmailTemplateCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as StandardEmailTemplateActions from "../../../actions/standardEmailTemplates";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    SelectFormInput,
    TextAreaFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { QALookupsLayout } from "../../index";

import "./QAEmailTemplatePage.scss";

interface QAEmailTemplatePageProps {
    form: Dtos.StandardEmailTemplate & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    standardEmailTemplate: Dtos.StandardEmailTemplate;
    lookups: Dtos.Lookup[];
    loadingStandardEmailTemplate: boolean;
    loadStandardEmailTemplateFailure: boolean;
    loadStandardEmailTemplateErrorCode: string;
    savingStandardEmailTemplate: boolean;
    saveStandardEmailTemplateSuccess: boolean;
    saveStandardEmailTemplateFailure: boolean;
    creatingStandardEmailTemplate: boolean;
    createStandardEmailTemplateSuccess: boolean;
    validatingStandardEmailTemplate: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearStandardEmailTemplate: StandardEmailTemplateActions.IStandardEmailTemplateClearActionCreator;
    loadStandardEmailTemplate: StandardEmailTemplateActions.IStandardEmailTemplateLoadByIdActionCreator;
    saveStandardEmailTemplate: StandardEmailTemplateActions.IStandardEmailTemplateSaveActionCreator;
    createStandardEmailTemplate: StandardEmailTemplateActions.IStandardEmailTemplateCreateActionCreator;
    validateStandardEmailTemplate: StandardEmailTemplateActions.IStandardEmailTemplateValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.standardEmailTemplate";

const QAEmailTemplatePage: FC<QAEmailTemplatePageProps> = ({
    form,
    formState,
    formProperties,
    standardEmailTemplate,
    lookups,
    loadingStandardEmailTemplate,
    loadStandardEmailTemplateFailure,
    loadStandardEmailTemplateErrorCode,
    savingStandardEmailTemplate,
    saveStandardEmailTemplateSuccess,
    saveStandardEmailTemplateFailure,
    creatingStandardEmailTemplate,
    createStandardEmailTemplateSuccess,
    validatingStandardEmailTemplate,
    validationFailures,
    permissions,
    clearStandardEmailTemplate,
    loadStandardEmailTemplate,
    saveStandardEmailTemplate,
    createStandardEmailTemplate,
    validateStandardEmailTemplate,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { standardEmailTemplateId } = useParams<{
        standardEmailTemplateId: string;
    }>();
    const standardEmailTemplateIdNumber = parseInt(standardEmailTemplateId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.StandardEmailTemplate =
            {} as Dtos.StandardEmailTemplate;

        loadForm(
            reduxFormName,
            Object.assign({ ...standardEmailTemplate }, extraData)
        );
    };


    // Load email template
    useEffect(() => {
        if (
            standardEmailTemplateIdNumber &&
            !standardEmailTemplate &&
            !loadingStandardEmailTemplate
        ) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(
                EmailTemplateBreadcrumbs(standardEmailTemplateIdNumber)
            );
            loadStandardEmailTemplate(standardEmailTemplateIdNumber);
        } else if (
            !standardEmailTemplateIdNumber &&
            !creatingStandardEmailTemplate &&
            !createStandardEmailTemplateSuccess
        ) {
            createStandardEmailTemplate();
            setBreadcrumbs(EmailTemplateCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (standardEmailTemplate && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        standardEmailTemplateIdNumber,
        standardEmailTemplate,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        EmailTemplateBreadcrumbs,
        loadStandardEmailTemplate,
        createStandardEmailTemplate,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingStandardEmailTemplate,
        creatingStandardEmailTemplate,
        createStandardEmailTemplateSuccess,
        EmailTemplateCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadStandardEmailTemplateFailure) {
            navigate(`/error/${loadStandardEmailTemplateErrorCode}`);
        }
    }, [
        loadStandardEmailTemplateFailure,
        loadStandardEmailTemplateErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (standardEmailTemplateIdNumber && standardEmailTemplate?.value) {
            setBreadcrumbs(
                EmailTemplateBreadcrumbs(
                    standardEmailTemplateIdNumber,
                    standardEmailTemplate?.value
                )
            );
        }
    }, [
        standardEmailTemplateIdNumber,
        standardEmailTemplate,
        setBreadcrumbs,
        EmailTemplateBreadcrumbs,
    ]);

    // Reload email template after saving
    useEffect(() => {
        if (!savingStandardEmailTemplate && saveStandardEmailTemplateSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Email template saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/qa/qaemailtemplates";
                navigate(url);
            } else if (!standardEmailTemplateIdNumber) {
                let url: string = `/settings/qa/qaemailtemplates/${standardEmailTemplate?.id}`;
                navigate(url);
                clearStandardEmailTemplate();
            }
        }
    }, [
        savingStandardEmailTemplate,
        saveStandardEmailTemplateSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingStandardEmailTemplate && saveStandardEmailTemplateFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingStandardEmailTemplate, saveStandardEmailTemplateFailure]);

    // Cleanup effect to clear the email template on component unmount
    useEffect(() => {
        return () => {
            clearStandardEmailTemplate();
            resetForm(reduxFormName);
        };
    }, [clearStandardEmailTemplate, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/qa/qaemailtemplates";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.StandardEmailTemplate) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.StandardEmailTemplate =
                {} as Dtos.StandardEmailTemplate;

            await saveStandardEmailTemplate(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.StandardEmailTemplate =
                {} as Dtos.StandardEmailTemplate;

            if (form && hasBeenSubmitted && !validatingStandardEmailTemplate) {
                await validateStandardEmailTemplate(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.QAEmailTemplatesUpdate,
                Dtos.Permission.QAEmailTemplatesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingStandardEmailTemplate ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "standardEmailTemplate",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "standardEmailTemplate",
                                        {
                                            standardEmailTypeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "StandardEmailTemplate",
                                                                "StandardEmailTypeId",
                                                                lookups
                                                            ),
                                                            form.standardEmailTypeId
                                                        ),
                                                },
                                                changeAction: (
                                                    model,
                                                    value,
                                                    _form,
                                                    _parentModel
                                                ) => {
                                                    changeForm(model, value);
                                                    changeForm(
                                                        reduxFormName +
                                                            ".standardEmailType",
                                                        null
                                                    );
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "standardEmailTemplate",
                                        {
                                            subject: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "standardEmailTemplate",
                                        {
                                            body: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "standardEmailTemplate",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "standardEmailTemplate",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "standardEmailTemplate",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingStandardEmailTemplate,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <QALookupsLayout
            loading={loadingStandardEmailTemplate}
            permission={Dtos.Permission.QAEmailTemplatesView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<QAEmailTemplatePageProps> => {
    let standardEmailTemplate: Dtos.StandardEmailTemplate | undefined = !(
        state.standardEmailTemplates.formData instanceof Array
    )
        ? state.standardEmailTemplates.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        standardEmailTemplate: standardEmailTemplate!,
        loadingStandardEmailTemplate:
            state.standardEmailTemplates.loadState &&
            state.standardEmailTemplates.loadState.status ==
                RequestState.Pending,
        loadStandardEmailTemplateFailure:
            state.standardEmailTemplates.loadState &&
            state.standardEmailTemplates.loadState.status ==
                RequestState.Failure,
        loadStandardEmailTemplateErrorCode:
            state.standardEmailTemplates.loadState.errorCode,
        savingStandardEmailTemplate:
            state.standardEmailTemplates.saveState &&
            state.standardEmailTemplates.saveState.status ==
                RequestState.Pending,
        saveStandardEmailTemplateSuccess:
            state.standardEmailTemplates.saveState &&
            state.standardEmailTemplates.saveState.status ==
                RequestState.Success,
        saveStandardEmailTemplateFailure:
            state.standardEmailTemplates.saveState &&
            state.standardEmailTemplates.saveState.status ==
                RequestState.Failure,
        creatingStandardEmailTemplate:
            state.standardEmailTemplates.createState &&
            state.standardEmailTemplates.createState.status ==
                RequestState.Pending,
        createStandardEmailTemplateSuccess:
            state.standardEmailTemplates.createState &&
            state.standardEmailTemplates.createState.status ==
                RequestState.Success,
        validatingStandardEmailTemplate:
            state.standardEmailTemplates.validateState &&
            state.standardEmailTemplates.validateState.status ==
                RequestState.Pending,
        permissions: permissions,
        lookups: state.standardEmailTemplates.lookups,
        form: state.reduxForms.standardEmailTemplate,
        formState: state.standardEmailTemplates.formState,
        formProperties: state.standardEmailTemplates.formProperties,
        validationFailures: standardEmailTemplate
            ? state.standardEmailTemplates.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<QAEmailTemplatePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadStandardEmailTemplate: bindActionCreators(
            StandardEmailTemplateActions.LoadStandardEmailTemplateById,
            dispatch
        ),
        clearStandardEmailTemplate: bindActionCreators(
            StandardEmailTemplateActions.Clear,
            dispatch
        ),
        saveStandardEmailTemplate: bindActionCreators(
            StandardEmailTemplateActions.SaveStandardEmailTemplate,
            dispatch
        ),
        createStandardEmailTemplate: bindActionCreators(
            StandardEmailTemplateActions.CreateStandardEmailTemplate,
            dispatch
        ),
        validateStandardEmailTemplate: bindActionCreators(
            StandardEmailTemplateActions.ValidateStandardEmailTemplate,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(QAEmailTemplatePage);
